import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import dayjs from 'dayjs'
import B2BLayout from '../../../layouts/b2b'
import { Form, Result, Space } from 'antd'
import { getColumns } from './columns'
import fetchAPI, { IHttpError } from '../../../../lib/utils/fetch-api'
import { useNavigate, useLocation } from 'react-router-dom'
import FiltersList from '../../../components/v2/filters'
import { Sorter } from '../../../interfaces'
import { objectToUrl } from '../../utils'
import { currentFiltersProps, DataType, Enums, IStage, TablePaginationPosition } from './interface'
import { createSelector } from 'reselect'
import { useSelector } from 'react-redux'
import {
  getJsonSettingsByKey,
  getColumnOrder,
  TableSettingsButton,
  TransferItem,
} from '../../../components/v2/table-settings-button'
import { getPrintPath } from '../../../components/v2/export-excel-button'
import { MeasurementPrintButton } from '../ui/mesurement-print-button/measurement-print-button'
import FiltersDrawer, { FiltersDrawerRef } from '../../../components/v2/filters-drawer'
import { getFilters } from './filters'
import { FiltersFastButtons } from '../../../components/v2/filters-fast-buttons'
import DataTable from '../../../components/data-table'
import { useHeaderActions } from '../../../contexts/header-actions-context'
import { HeaderAction } from '../../../contexts/header-actions-context/interface'
import { FilterOutlined } from '@ant-design/icons'
import StickyToolbar from '../../../layouts/b2b/sticky-toolbar'
import { getMobileFields } from './mobile-fields'
import { useMediaQuery } from 'react-responsive'
import { ResultStatusType } from 'antd/lib/result'
import './index.css'

const createMenuCounters = createSelector(
  (state: any) => state.menuV2,
  counters => counters,
)

const createSession = createSelector(
  (state: any) => state.session,
  sessionData => sessionData,
)

function parseUrlSearchParams(search: string): currentFiltersProps {
  const urlParams = new URLSearchParams(search)
  return {
    phone: urlParams.get('phone') || undefined,
    manager: Number(urlParams.get('manager')) || undefined,
    id: Number(urlParams.get('id')) || undefined,
    brand: urlParams.get('brand') || undefined,
    stage: urlParams.get('stage') || undefined,
    dates: urlParams.get('dates') || undefined,
    measurement_dates: urlParams.get('measurement_dates') || undefined,
    office: urlParams.get('office') || undefined,
    responsible: urlParams.get('responsible') || undefined,
    production_number: urlParams.get('production_number') || undefined,
  }
}

const MeasurementsList = () => {
  const [filtersForm] = Form.useForm()
  const initialPageSize = (() => {
    const savedPageSize = localStorage.getItem('measurementsPageSize')
    const parsedPageSize = Number(savedPageSize)
    return parsedPageSize && !isNaN(parsedPageSize) && parsedPageSize <= 150 ? parsedPageSize : 50
  })()

  const headerRef = useRef<HTMLDivElement | null>(null)
  const filtersDrawerRef = useRef<FiltersDrawerRef | null>(null)
  const tableSettingsRef = useRef<{ open: () => void } | null>(null)
  const measurementPrintRef = useRef<{ onPrintClick: () => Promise<void> } | null>(null)
  const isInitialLoadRef = useRef(true)
  const isLoadingStagesRef = useRef(false)
  const isLoadingFiltersRef = useRef(false)
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  const [data, setData] = useState<DataType[]>([])
  const [availableFilters, setAvailableFilters] = useState<Enums>({
    manager: [],
    stage: [],
    brand: [],
    responsible: [],
  })
  const [urlParams] = useState(Object.fromEntries(new URLSearchParams(search)))
  const [currentFilters, setCurrentFilters] = useState<currentFiltersProps>({})
  const { sessionData } = useSelector(createSession)
  const { setHeaderActions } = useHeaderActions()

  const isMeasurer = sessionData?.organization?.type === 276
  const isGoLeader = sessionData?.roles.includes(16)
  const isBrandLeader = !isGoLeader && sessionData?.roles.includes(32)

  const [loading, setLoading] = useState(false)
  const [bottomCenter] = useState<TablePaginationPosition>('bottomCenter')
  const [stages, setStages] = useState<any>(null)
  const [sorter, setSorter] = useState<Sorter>({})
  const [pagination, setPagination] = useState({
    current: Number(urlParams?.page) || 1,
    pageSize: initialPageSize,
    position: [bottomCenter],
    showSizeChanger: true,
    total: 0,
  })
  const { counters } = useSelector(createMenuCounters)
  const [localCounter, setLocalCounter] = useState<null | number>(null)
  const jsonSettings = useSelector(state => getJsonSettingsByKey(state, pathname))
  const isMobile = useMediaQuery({ maxWidth: 768 })

  interface IError {
    code?: ResultStatusType
    message?: string
  }
  const [error, setError] = useState<null | IError>(null)

  const columns = useMemo(
    () => getColumns(isGoLeader, isMeasurer, sorter),
    [isGoLeader, isMeasurer, sorter],
  )

  const viewColumns = useMemo(() => {
    const enhanceColumn = (column: any) => {
      if (!('dataIndex' in column) || !column.editable) {
        return column
      }
      return {
        ...column,
      }
    }

    const columnOrder = getColumnOrder(jsonSettings)

    if (columnOrder.length > 0) {
      const enhancedColumns = columns.map(enhanceColumn)
      let result: any[] = []
      for (let i = 0; i < columnOrder.length; i++) {
        const item = enhancedColumns.find(column => columnOrder[i] === column.dataIndex)
        if (item) {
          result.push(item)
        }
      }

      const actionColumn = enhancedColumns.find(column => column.key === 'operation')
      if (actionColumn && !result.find(col => col.key === 'operation')) {
        result.push(actionColumn)
      }

      return result
    }

    return columns.map(enhanceColumn)
  }, [columns, jsonSettings])

  const mobileFields = useMemo(() => getMobileFields(), [])

  const excelPath = useMemo(() => {
    return getPrintPath('/api/measurements', new URLSearchParams(search))
  }, [search])

  const settingsColumns = useMemo(
    () =>
      columns.map(column => ({
        key: (column as any)?.dataIndex,
        title: column?.title,
      })),
    [columns],
  ) as TransferItem[]

  useEffect(() => {
    const newFilters = parseUrlSearchParams(search)
    setCurrentFilters(newFilters)
  }, [search])

  const areObjectsIdentical = (obj1: Object, obj2: Object): boolean => {
    if (obj1 === obj2) {
      return true
    }
    if (typeof obj1 !== 'object' || typeof obj2 !== 'object' || obj1 == null || obj2 == null) {
      return false
    }
    const keys1 = Object.keys(obj1)
    const keys2 = Object.keys(obj2)
    if (keys1.length !== keys2.length) {
      return false
    }
    for (const key of keys1) {
      if (!keys2.includes(key) || !areObjectsIdentical(obj1[key], obj2[key])) {
        return false
      }
    }
    return true
  }

  useEffect(() => {
    const newCounterData = counters?.['/measurements']
    if (newCounterData && !localCounter) {
      setLocalCounter(newCounterData)
    }
    if (newCounterData && localCounter && newCounterData !== localCounter) {
      setLocalCounter(newCounterData)
      getData()
    }
  }, [counters])

  useEffect(() => {
    if (!stages) {
      getStages()
    }
  }, [stages])

  useEffect(() => {
    if (stages && stages.length > 0 && isInitialLoadRef.current) {
      getAvailableFilters()
      isInitialLoadRef.current = false
    }
  }, [stages])

  useEffect(() => {
    if (stages && stages.length > 0) {
      getData()
    }
  }, [search, stages])

  const getStages = useCallback(() => {
    if (!stages && !isLoadingStagesRef.current) {
      isLoadingStagesRef.current = true
      fetchAPI('/api/get-stages/measurement')
        .then(data => {
          setStages(data)
          isLoadingStagesRef.current = false
        })
        .catch(() => {
          console.error('Не удалось получить стадии')
          setError({ code: 'error', message: 'Не удалось получить стадии' })
          isLoadingStagesRef.current = false
        })
    }
  }, [stages])

  const deleteRecord = useCallback(
    currentId => {
      fetchAPI(`/api/measurement/${currentId}`, { method: 'DELETE' })
        .then(() => {
          const urlParams = Object.fromEntries(new URLSearchParams(search))
          fetchAPI(`/api/measurements${objectToUrl(urlParams)}`)
            .then(({ data, pagination: paginationData, sorter: sorterData }) => {
              if (data && stages) {
                setData(
                  data.map(item => ({
                    ...item,
                    stage: stages.find(stage => stage.code === item.stage),
                  })),
                )
                setPagination(prevState => ({
                  ...prevState,
                  ...paginationData,
                }))
                if (sorterData) {
                  setSorter(sorterData)
                }
              }
            })
            .catch(() => {
              console.error('Не удалось обновить список после удаления')
            })
        })
        .catch(() => {
          console.error('Не удалось удалить запись')
        })
    },
    [search, stages],
  )

  const getAvailableFilters = useCallback(() => {
    if (!stages || stages.length === 0 || isLoadingFiltersRef.current) {
      return
    }
    isLoadingFiltersRef.current = true
    fetchAPI('/api/measurement-enums')
      .then(data => {
        const filters = {
          ...data,
          stage: stages.map((i: IStage) => ({ label: i.name, value: i.code })),
        }
        setAvailableFilters(filters)
        isLoadingFiltersRef.current = false
      })
      .catch(e => {
        console.error('Не удалось получить список доступных фильтров', e)
        isLoadingFiltersRef.current = false
      })
  }, [stages])

  const getData = useCallback(async () => {
    if (!stages || stages.length === 0) {
      return
    }
    try {
      setLoading(true)
      setError(null)
      const urlParams = Object.fromEntries(new URLSearchParams(search))
      await fetchAPI(`/api/measurements${objectToUrl(urlParams)}`).then(
        ({ data, pagination, sorter }) => {
          if (data) {
            setData(
              data.map(item => {
                return {
                  ...item,
                  stage: stages.find(stage => stage.code === item.stage),
                }
              }),
            )
            setPagination(prevState => ({
              ...prevState,
              ...pagination,
            }))
            setSorter(sorter)
          }
        },
      )
    } catch (error) {
      console.error('Ошибка при получении данных:', error)
      setError({
        message: (error as IHttpError)?.message || 'Неизвестная ошибка',
        code: ((error as IHttpError)?.statusCode || 'error') as ResultStatusType,
      })
    } finally {
      setLoading(false)
    }
  }, [stages, search])

  const handleTableChange = useCallback(
    (_pagination, _filters, sorter) => {
      const currentUrlParams = Object.fromEntries(new URLSearchParams(search))
      setPagination(prevState => ({ ...prevState, ...pagination }))
      const current: Record<string, any> = {
        ...currentUrlParams,
        sort_order: sorter?.order,
        sort_field: sorter?.field,
        page: pagination.current,
        pageSize: pagination.pageSize,
      }
      if (current?.page === 1) {
        delete current.page
      }
      if (current?.pageSize === 10) {
        delete current.pageSize
      }
      if (current?.sort_order === undefined) {
        delete current.sort_order
        delete current.sort_field
      }
      const urlParams = new URLSearchParams(current).toString()
      navigate({ search: urlParams })
    },
    [search, navigate, pagination],
  )

  const handlePageChange = (page: number, pageSize: number) => {
    setPagination(prevState => ({
      ...prevState,
      current: Number(page) || 1,
      pageSize: pageSize > 150 ? 150 : pageSize,
    }))

    if (!isNaN(pageSize)) {
      localStorage.setItem('measurementsPageSize', String(pageSize))
    }

    const currentUrlParams = new URLSearchParams(search)
    if (page && page !== 1) {
      currentUrlParams.set('page', page.toString())
    } else {
      currentUrlParams.delete('page')
    }
    if (!isNaN(pageSize)) {
      if (pageSize && pageSize !== 10) {
        currentUrlParams.set('pageSize', pageSize.toString())
      } else {
        currentUrlParams.delete('pageSize')
      }
    }
    navigate({ search: currentUrlParams.toString() })
  }

  const handleRowClick = record => {
    const { id } = record
    navigate(`/measurements/${id}`)
  }

  const changeFiltersHandler = useCallback(
    filters => {
      let tmpFilter = { ...(filters || {}), pageSize: pagination.pageSize }
      const newUrlParams = new URLSearchParams(tmpFilter).toString()
      navigate({ search: newUrlParams })
    },
    [pagination],
  )

  const filters = useMemo(() => {
    return getFilters({ currentFilters, availableFilters, isGoLeader, isBrandLeader, isMeasurer })
  }, [availableFilters, currentFilters, isGoLeader, isBrandLeader, isMeasurer])

  const fastButtons = useMemo(() => {
    const today = dayjs().format('YYYY.MM.DD')
    const todayRange = `${today}-${today}`
    const currentUserId = sessionData?.user?.id

    const baseButtons: { name: string; code: string; value: string }[] = [
      { name: 'Замеры на сегодня', code: 'dates', value: todayRange },
      { name: 'Новые заявки', code: 'stage', value: 'new' },
      { name: 'В работе', code: 'stage', value: 'work' },
      { name: 'Выполнено', code: 'stage', value: 'done' },
    ]

    if (!isMeasurer && currentUserId) {
      return [{ name: 'Мои заявки', code: 'manager', value: String(currentUserId) }, ...baseButtons]
    }

    return baseButtons
  }, [isMeasurer, sessionData?.user?.id])

  useEffect(() => {
    const actions: HeaderAction[] = []

    // Фильтры
    if (filtersDrawerRef) {
      actions.push({
        key: 'filters',
        icon: <FilterOutlined />,
        onClick: () => {
          filtersDrawerRef.current?.toggle()
        },
        tooltip: 'Фильтры',
      })
    }

    if (isMobile) {
      // Выгрузка в Excel
      /*if (measurementPrintRef) {
        actions.push({
          key: 'excel',
          icon: <DownloadOutlined />,
          onClick: () => {
            measurementPrintRef.current?.onPrintClick()
          },
          tooltip: 'Выгрузка в Excel'
        })
      }

      // Настройка колонок
      if (settingsColumns) {
        actions.push({
          key: 'settings',
          icon: <SettingOutlined />,
          onClick: () => {
            tableSettingsRef.current?.open()
          },
          tooltip: 'Настройка колонок'
        })
      }*/
    }

    setHeaderActions(actions)

    return () => {
      setHeaderActions([])
    }
  }, [setHeaderActions, settingsColumns, isMobile, measurementPrintRef])

  const dataWithActions = useMemo(() => {
    return data.map(item => ({
      ...item,
      deleteRecord,
    }))
  }, [data, deleteRecord])

  return (
    <B2BLayout pageTitle={'Замеры'} isViewScrollButton>
      {error && <Result status={error.code} title="Ошибка" subTitle={error.message} />}
      {!error && stages && (
        <>
          <StickyToolbar
            ref={headerRef}
            collapsible={
              <Space
                style={{ width: '100%', justifyContent: 'space-between' }}
                align={'start'}
                wrap
              >
                <Space align={'start'} wrap>
                  <FiltersList
                    filters={filters.filter(filter =>
                      ['id', 'stage', 'production_number'].includes(filter.name),
                    )}
                    onChange={changeFiltersHandler}
                    form={filtersForm}
                  />
                </Space>
                <FiltersDrawer
                  filters={filters}
                  onChange={changeFiltersHandler}
                  currentFilters={currentFilters}
                  ref={filtersDrawerRef}
                />
              </Space>
            }
            persistent={
              isMobile ? (
                <FiltersFastButtons filters={fastButtons} onChange={changeFiltersHandler} />
              ) : (
                <Space
                  style={{ width: '100%', justifyContent: 'space-between' }}
                  align={'center'}
                  wrap
                >
                  <FiltersFastButtons filters={fastButtons} onChange={changeFiltersHandler} />
                  <Space align={'center'} wrap>
                    <MeasurementPrintButton
                      stages={stages}
                      columns={viewColumns}
                      path={excelPath}
                      ref={measurementPrintRef}
                    />
                    <TableSettingsButton items={settingsColumns} ref={tableSettingsRef} />
                  </Space>
                </Space>
              )
            }
          />
          <DataTable
            columns={viewColumns}
            mobileFields={mobileFields}
            data={dataWithActions}
            loading={loading}
            rowKey={r => String(r.id)}
            highlightLastRowPrefix={'measurement'}
            pagination={pagination}
            onRowClick={handleRowClick}
            onPageChange={handlePageChange}
            onTableChange={handleTableChange}
            className={'measurement-table'}
            rowClassName={record => {
              if (record.repeat_number > 0) return 'table-row-repeat'
              return ''
            }}
            headerRef={headerRef}
            maxLines={1}
            bordered
            scroll={{ x: 'max-content' }}
          />
        </>
      )}
    </B2BLayout>
  )
}

export default MeasurementsList
